import { ReactNode, useMemo } from "react"
import { Decoder } from "./Coding.ts"
import { noHit } from "./Entity.ts"
import { useBufferObject } from "./hooks/useBufferObject.ts"
import { useDrawCall } from "./hooks/useDrawCall.tsx"
import { useGL } from "./hooks/useGL.ts"
import { useLighting } from "./hooks/useLighting.ts"
import { useProgram } from "./hooks/useProgram.ts"
import { useResource } from "./hooks/useResource.ts"
import { useTexture } from "./hooks/useTexture.ts"
import { useUniformBlock } from "./hooks/useUniformBlock.ts"
import fragmentSource from './MeshView.fs?raw'
import vertexSource from './MeshView.vs?raw'
import { PosedMannequin } from "./PosedSkeleton.ts"

// This needs to ONE LESS THAN the denominator in the thresholds matrix in the fragment shader.
export const MeshView_opacitySteps = 16.0

export interface MeshViewSettings {
  readonly wireframe: boolean
  readonly opacity: number
  readonly antiShimmerEnabled: boolean
}

export function decodeMeshViewSettingsFrom(decoder: Decoder): MeshViewSettings {
  const container = decoder.keyedDecoder()
  return {
    wireframe: container.decodeBoolean('wireframe', true),
    opacity: container.decodeNumber('opacity', 0.5),
    antiShimmerEnabled: container.decodeBoolean('antiShimmer', true),
  }
}

export const MeshViewSettings_fallback: MeshViewSettings = {
  wireframe: true,
  opacity: 0.5,
  antiShimmerEnabled: true,
}

export function MeshView(props: {
  posedMannequin: PosedMannequin
  settings: MeshViewSettings
  selectedBone: number | null
}): ReactNode {
  const { posedMannequin, settings, selectedBone } = props
  const { mesh, skinBinding } = posedMannequin.mannequin

  const gl = useGL()
  const program = useProgram('2D triangle', gl, vertexSource, fragmentSource)
  const locations = useMemo(() => ({
    // Vertex shader inputs.
    a_pMesh: gl.getAttribLocation(program, 'a_pMesh'),
    a_nMesh: gl.getAttribLocation(program, 'a_nMesh'),
    a_uv: gl.getAttribLocation(program, 'a_uv'),
    a_boneIndexes: gl.getAttribLocation(program, 'a_boneIndexes'),
    a_boneWeights: gl.getAttribLocation(program, 'a_boneWeights'),

    // Vertex shader uniforms.
    u_clipFromWorld: gl.getUniformLocation(program, 'u_clipFromWorld'),
    u_mouseHover: gl.getUniformLocation(program, 'u_mouseHover'),
    u_selectedBone: gl.getUniformLocation(program, 'u_selectedBone'),

    // Fragment shader uniforms.
    u_sampler: gl.getUniformLocation(program, 'u_sampler'),
    u_opacity: gl.getUniformLocation(program, 'u_opacity'),
    u_wireframeEnabled: gl.getUniformLocation(program, 'u_wireframeEnabled'),
    u_antiShimmerEnabled: gl.getUniformLocation(program, 'u_antiShimmerEnabled'),
    u_useSampler: gl.getUniformLocation(program, 'u_useSampler'),
  }), [gl, program])

  const vertexCount = mesh.positions.length / 3
  const vbo_pMesh = useBufferObject('vbo_pMesh', gl, gl.ARRAY_BUFFER)
  const vbo_nMesh = useBufferObject('vbo_nMesh', gl, gl.ARRAY_BUFFER)
  const vbo_uv = useBufferObject('vbo_uv', gl, gl.ARRAY_BUFFER)
  const vbo_boneIndexes = useBufferObject('vbo_boneIndexes', gl, gl.ARRAY_BUFFER)
  const vbo_boneWeights = useBufferObject('vbo_boneWeights', gl, gl.ARRAY_BUFFER)

  const ubo_worldFromMeshTransforms = useUniformBlock('Pose', gl, program)
  const ubo_worldFromMeshNormalTransforms = useUniformBlock('PoseNormal', gl, program)

  const Transform_byteSize = 16 * 4 // mat4 = 16 floats * 4 bytes/float
  const MaxBones = ubo_worldFromMeshTransforms.byteSize / Transform_byteSize

  const hasTextureMap = mesh.textureMap !== null

  const vao = useResource(function* () {
    const vao = gl.createVertexArray()

    gl.bindVertexArray(vao)

    gl.enableVertexAttribArray(locations.a_pMesh)
    vbo_pMesh.bind(() => {
      gl.vertexAttribPointer(locations.a_pMesh, 3, gl.FLOAT, false, 0, 0)
    })

    gl.enableVertexAttribArray(locations.a_nMesh)
    vbo_nMesh.bind(() => {
      gl.vertexAttribPointer(locations.a_nMesh, 3, gl.FLOAT, false, 0, 0)
    })

    if (hasTextureMap) {
      gl.enableVertexAttribArray(locations.a_uv)
      vbo_uv.bind(() => {
        gl.vertexAttribPointer(locations.a_uv, 2, gl.FLOAT, false, 0, 0)
      })
    } else {
      gl.disableVertexAttribArray(locations.a_uv)
    }

    gl.enableVertexAttribArray(locations.a_boneIndexes)
    vbo_boneIndexes.bind(() => {
      gl.vertexAttribIPointer(locations.a_boneIndexes, 4, gl.INT, 0, 0)
    })

    gl.enableVertexAttribArray(locations.a_boneWeights)
    vbo_boneWeights.bind(() => {
      gl.vertexAttribPointer(locations.a_boneWeights, 4, gl.FLOAT, false, 0, 0)
    })

    gl.bindVertexArray(null)
    gl.bindBuffer(gl.ARRAY_BUFFER, null)

    yield vao

    gl.deleteVertexArray(vao)
  }, [gl, program, vbo_pMesh, vbo_nMesh, vbo_uv, vbo_boneIndexes, vbo_boneWeights, hasTextureMap])

  const runLighting = useLighting(gl, program)

  const worldFromMeshTransformData = useMemo(() => {
    let data = new Float32Array(MaxBones * 16)
    data.set([...posedMannequin.worldFromMesh_posedTransforms().values().flatMap(m => m)])
    return data
  }, [posedMannequin])

  const wordFromMeshNormalTransformData = useMemo(() => {
    let data = new Float32Array(MaxBones * 16)
    data.set([...posedMannequin.worldFromMesh_posedTransforms().values().flatMap(m => m.basis.normalTransform.asMat4)])
    return data
  }, [posedMannequin])

  const texture = useTexture('MeshView', gl)

  useDrawCall('MeshView', ({ gl, clipFromWorld, mouseHit }) => {
    // Apparently, effects can get disposed in an order that destroys program and then runs this effect again.
    if (settings.opacity === 0.0 || !gl.isProgram(program) || !gl.isVertexArray(vao) || vertexCount === 0) {
      return
    }

    if (mouseHit.who !== posedMannequin) {
      mouseHit = noHit
    }

    // Force these to be available in the debugger.
    // console.log(`${vec3.name} ${vec4.name} ${mat3.name} ${mat4.name}`)

    vbo_pMesh.fill(mesh.positions)
    vbo_nMesh.fill(mesh.normals)
    if (hasTextureMap) {
      vbo_uv.fill(mesh.textureMap.uvData)
    }
    vbo_boneIndexes.fill(skinBinding.boneIndexes)
    vbo_boneWeights.fill(skinBinding.boneWeights)

    gl.useProgram(program)

    ubo_worldFromMeshTransforms.use(worldFromMeshTransformData, () => {
      ubo_worldFromMeshNormalTransforms.use(wordFromMeshNormalTransformData, () => {
        runLighting(() => {

          gl.bindVertexArray(vao)

          gl.uniformMatrix4fv(locations.u_clipFromWorld, false, Float32Array.from(clipFromWorld))
          gl.uniform3f(locations.u_mouseHover, ...mouseHit.where.tuple)
          gl.uniform1i(locations.u_selectedBone, selectedBone ?? -1)

          gl.uniform1f(locations.u_opacity, settings.opacity)
          gl.uniform1i(locations.u_wireframeEnabled, settings.wireframe ? 1 : 0)
          gl.uniform1i(locations.u_antiShimmerEnabled, settings.antiShimmerEnabled ? 1 : 0)
          gl.uniform1i(locations.u_useSampler, hasTextureMap ? 1 : 0)

          texture.use(posedMannequin.mannequin.mesh.textureMap?.image ?? null, locations.u_sampler!)

          gl.drawArrays(gl.TRIANGLES, 0, vertexCount)
        })
      })
    })

    gl.bindBuffer(gl.ARRAY_BUFFER, null)
    gl.useProgram(null)
  }, [
    program, locations, vao, settings, runLighting,
    posedMannequin, selectedBone, texture, ``
  ])

  return undefined
}
